import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/AdminLayout";
import { Pill } from "@/components/Badges";
import { api, errMsg, fmtDate } from "@/lib/api";

const BLANK = { code: "", percent: 10, expires_on: "", max_uses: 0, label: "" };

function promoState(p) {
  const today = new Date(Date.now() - 10 * 3600 * 1000).toISOString().slice(0, 10);
  if (!p.active) return ["slate", "Off"];
  if (p.expires_on && p.expires_on < today) return ["red", "Expired"];
  if (p.max_uses && p.uses >= p.max_uses) return ["amber", "Used up"];
  return ["emerald", "Live"];
}

export default function Promos() {
  const [list, setList] = useState([]);
  const [f, setF] = useState(BLANK);

  const load = () => api.get("/admin/promos").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/promos", { ...f, percent: Number(f.percent), max_uses: Number(f.max_uses), expires_on: f.expires_on || null });
      toast.success(`Code ${data.code} created`);
      setF(BLANK);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.toUpperCase() : e.target.value });

  return (
    <div>
      <PageHeader eyebrow="Specials" title="Discount codes" />
      <form onSubmit={create} className="mb-3 grid grid-cols-2 items-end gap-4 rounded-lg border border-border bg-card p-6 lg:grid-cols-[1fr_110px_160px_130px_1fr_auto]">
        <div className="space-y-1.5"><Label>Code (blank = random)</Label><Input value={f.code} onChange={set("code")} placeholder="SUMMER20" className="font-mono-data tracking-widest" data-testid="promo-code-input" /></div>
        <div className="space-y-1.5"><Label>% off</Label><Input type="number" min="1" max="100" step="0.5" required value={f.percent} onChange={set("percent")} data-testid="promo-percent-input" /></div>
        <div className="space-y-1.5"><Label>Expires (optional)</Label><Input type="date" value={f.expires_on} onChange={set("expires_on")} data-testid="promo-expires-input" /></div>
        <div className="space-y-1.5"><Label>Max uses (0 = ∞)</Label><Input type="number" min="0" value={f.max_uses} onChange={set("max_uses")} data-testid="promo-max-uses-input" /></div>
        <div className="col-span-2 space-y-1.5 lg:col-span-1"><Label>Note (optional)</Label><Input value={f.label} onChange={set("label")} placeholder="e.g. Summer special" data-testid="promo-label-input" /></div>
        <Button type="submit" className="col-span-2 gap-2 lg:col-span-1" data-testid="promo-create-button"><Plus className="h-4 w-4" />Create code</Button>
      </form>
      <p className="mb-8 text-xs text-muted-foreground">Codes never discount items already on sale or reconstitution fees. A code still works through the end of its expiry date. Cancelling an order gives the use back.</p>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Off</TableHead><TableHead>Used</TableHead><TableHead>Expires</TableHead><TableHead>Note</TableHead><TableHead>Status</TableHead><TableHead>On</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {list.map((p) => {
              const [c, label] = promoState(p);
              return (
                <TableRow key={p.code} data-testid={`promo-row-${p.code}`}>
                  <TableCell className="font-mono-data text-base tracking-widest text-emerald-400">{p.code}</TableCell>
                  <TableCell className="font-mono-data">{p.percent}%</TableCell>
                  <TableCell className="font-mono-data" data-testid={`promo-uses-${p.code}`}>{p.uses} / {p.max_uses === 0 ? "∞" : p.max_uses}</TableCell>
                  <TableCell className="text-muted-foreground">{p.expires_on ? fmtDate(`${p.expires_on}T12:00:00`) : "Never"}</TableCell>
                  <TableCell>{p.label || "—"}</TableCell>
                  <TableCell><Pill color={c} testId={`promo-status-${p.code}`}>{label}</Pill></TableCell>
                  <TableCell><Switch checked={p.active} onCheckedChange={(v) => act(() => api.patch(`/admin/promos/${p.code}`, { active: v }))} data-testid={`promo-active-switch-${p.code}`} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => navigator.clipboard?.writeText(p.code).then(() => toast.success("Code copied"))} data-testid={`promo-copy-${p.code}`}><Copy className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => act(() => api.delete(`/admin/promos/${p.code}`), "Code deleted")} data-testid={`promo-delete-${p.code}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {list.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="promos-empty">No discount codes yet.</p>}
      </div>
    </div>
  );
}
